import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  Typography,
  useTheme,
  Link as MuiLink,
  IconButton,
  Drawer,
  Divider,
  Stack,
  Slide,
  useScrollTrigger,
  List,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ArticleIcon from "@mui/icons-material/Article";
import LoginIcon from "@mui/icons-material/Login";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Link as RouterLink } from "react-router-dom";
import Logo from "./common/Logo";

const navLinkSx = (theme) => ({
  position: "relative",
  fontWeight: 600,
  fontSize: "1rem",
  letterSpacing: "-0.3px",
  color: theme.palette.text.primary,
  px: 0.75,
  py: 0.5,
  textDecoration: "none",
  transition: "color 0.18s cubic-bezier(.45,0,.55,1)",
  "&:after": {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: 2,
    width: "100%",
    height: 2,
    borderRadius: 2,
    background: theme.palette.primary.main,
    opacity: 0,
    transform: "scaleX(0)",
    transition: "all 0.23s cubic-bezier(.76,.06,.27,.98)",
  },
  "&:hover:after, &:focus:after": {
    opacity: 1,
    transform: "scaleX(1)",
  },
  "&:hover, &:focus": {
    color: theme.palette.primary.main,
    background: "none",
    outline: "none",
  },
});

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger({ threshold: 64 });
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export default function PublicNavBar() {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => setMobileOpen((v) => !v);

  const drawerContent = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        p: 3,
        minWidth: 250,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        background: "rgba(255,255,255,0.97)",
        height: "100%",
        borderTopLeftRadius: 28,
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          fontFamily: "Inter, Poppins, sans-serif",
          mb: 2,
          color: "#2563eb",
          letterSpacing: -1,
        }}
      >
        SortlyBooks
      </Typography>
      <Divider sx={{ width: "80%", mb: 2 }} />
      <Stack spacing={1.5} sx={{ width: "100%" }}>
        <Button
          component={RouterLink}
          to="/pricing"
          startIcon={<AttachMoneyIcon />}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            justifyContent: "flex-start",
            px: 2,
          }}
        >
          Pricing
        </Button>
        <Button
          component={RouterLink}
          to="/blog"
          startIcon={<ArticleIcon />}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            justifyContent: "flex-start",
            px: 2,
          }}
        >
          Blog
        </Button>
        <Button
          component={RouterLink}
          to="/login"
          startIcon={<LoginIcon />}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            justifyContent: "flex-start",
            px: 2,
          }}
        >
          Login
        </Button>
        <Button
          component={RouterLink}
          to="/contact"
          startIcon={<EmailOutlinedIcon />}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            justifyContent: "flex-start",
            px: 2,
          }}
        >
          Contact
        </Button>
        <Button
          component={RouterLink}
          to="/signup"
          startIcon={<RocketLaunchIcon />}
          variant="contained"
          fullWidth
          sx={{
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 700,
            fontSize: "1.08rem",
            mt: 1,
            background: "linear-gradient(90deg,#22c55e 70%,#4ade80 100%)",
            boxShadow: "0 2px 16px #bbf7d040",
            "&:hover": { background: "#16a34a" },
            py: 1.4,
            letterSpacing: 0.2,
          }}
        >
          Sign Up
        </Button>
      </Stack>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: "rgba(255,255,255,0.86)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            color: theme.palette.text.primary,
            borderBottom: "1.5px solid #f1f5f9",
            boxShadow: "0 2px 24px 0 #e0e7ef22",
            zIndex: theme.zIndex.drawer + 2,
            transition: "background 0.28s cubic-bezier(.42,0,.58,1)",
          }}
        >
          <Toolbar
            sx={{
              justifyContent: "space-between",
              px: { xs: 2, md: 7 },
              minHeight: 64,
              alignItems: "center",
            }}
          >
            {/* Logo / Brand */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                gap: 1,
              }}
            >
              <Logo sx={{ width: 40, height: 40, mr: 1.5, borderRadius: "50%" }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  fontFamily: "Inter, Poppins, sans-serif",
                  color: "#2563eb",
                  letterSpacing: -1,
                  fontSize: { xs: "1.15rem", md: "1.45rem" },
                  ml: -1,
                }}
              >
                SortlyBooks
              </Typography>
            </Box>

            {/* Desktop Nav */}
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 2.7,
                ml: 2,
              }}
            >
              <MuiLink
                component={RouterLink}
                to="/pricing"
                underline="none"
                sx={navLinkSx(theme)}
              >
                Pricing
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/blog"
                underline="none"
                sx={navLinkSx(theme)}
              >
                Blog
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/login"
                underline="none"
                sx={navLinkSx(theme)}
              >
                Login
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/contact"
                underline="none"
                sx={navLinkSx(theme)}
              >
                Contact
              </MuiLink>
              <Button
                component={RouterLink}
                to="/signup"
                variant="contained"
                disableElevation
                sx={{
                  ml: 2,
                  borderRadius: 999,
                  px: 3.3,
                  py: 1.3,
                  fontWeight: 700,
                  background: "linear-gradient(90deg,#22c55e 70%,#4ade80 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 18px 0 #bbf7d040, 0 0.5px 0 #15803d inset",
                  fontSize: "1.07rem",
                  letterSpacing: 0.13,
                  textTransform: "none",
                  transition: "box-shadow 0.18s",
                  animation:
                    "pulse 1.7s infinite cubic-bezier(.66,0,.33,1) alternate",
                  "@keyframes pulse": {
                    from: { boxShadow: "0 2px 18px 0 #bbf7d033" },
                    to: { boxShadow: "0 2px 28px 0 #bbf7d077" },
                  },
                  "&:hover": {
                    background:
                      "linear-gradient(90deg,#16a34a 80%,#22c55e 100%)",
                    boxShadow: "0 2px 38px 0 #22c55e33",
                  },
                }}
              >
                Sign Up
              </Button>
            </Box>

            {/* Mobile Burger */}
            <IconButton
              color="primary"
              edge="end"
              onClick={handleDrawerToggle}
              sx={{ display: { md: "none" }, ml: 1 }}
              aria-label="menu"
            >
              <MenuIcon fontSize="large" />
            </IconButton>
          </Toolbar>
        </AppBar>
      </HideOnScroll>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 32,
            borderBottomLeftRadius: 20,
            boxShadow: "0 8px 48px #0ea5e980",
            pt: 1,
            bgcolor: "#f9fafb",
          },
        }}
      >
        {drawerContent}
      </Drawer>
      {/* No unnecessary minHeight spacer below */}
    </>
  );
}
